import { useState } from 'react';
import Seo from '@/components/Seo';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ToolLongForm } from '@/components/tools/ToolLongForm';
import { ToolFAQ } from '@/components/tools/ToolFAQ';
import { AdSlot } from '@/components/ads/AdSlot';
import { getThumbnailInfo, thumbnailDownloadUrl } from '@/lib/api';
import { AlertCircle, Download, Loader2, Youtube, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const FAQS = [
    { q: 'Which YouTube URL formats are supported?', a: 'Standard watch URLs (youtube.com/watch?v=…), short links (youtu.be/…), Shorts (youtube.com/shorts/…), embed and live URLs, and even a bare 11-character video ID.' },
    { q: 'Do all videos have max-resolution thumbnails?', a: 'Most modern uploads have a 1280×720 maxresdefault version. Some older or low-resolution videos only expose the smaller sizes — the tool marks any missing size as “unavailable” before you try to download.' },
    { q: 'Is this affiliated with YouTube?', a: 'No. We simply reach the public thumbnail URLs that YouTube already serves for every uploaded video.' },
    { q: 'Can I use these images anywhere?', a: 'YouTube thumbnails are copyrighted by their creators. Use them for previews, editorial coverage, or your own videos — not for reposting content as your own.' },
    { q: 'Do you store the URL I paste?', a: 'No. The URL is used once, live, to look up the thumbnails, and is not stored or logged.' },
];

const HOW = [
    'Paste any YouTube URL — or just the 11-character video ID — and press Fetch. UtilityTool extracts the ID, then asks YouTube’s public thumbnail service which sizes exist for that video: from the small default thumbnail all the way up to the 1280×720 max-resolution version.',
    'You get a grid of available sizes, each with the exact pixel dimensions. Click any Download button and we stream the thumbnail through our server as an image download, which sidesteps the CORS restrictions that would otherwise block a direct download from your browser.',
    'The tool works with regular videos, Shorts, embedded videos, and the live-stream URL format. It also handles the mobile subdomain (m.youtube.com) and the privacy-friendly youtube-nocookie.com variant.',
];
const BENEFITS = [
    'For creators, quick access to their own thumbnails is useful for cross-posting to a portfolio, a channel-art recap, or a newsletter. Instead of digging through Studio for the exact PNG, you paste your public URL and download the size you need.',
    'For educators and reviewers, thumbnails act as compact “hero images” in blog posts and slide decks. Grabbing them at their native resolution keeps the visuals crisp and consistent with the video the audience is being pointed to.',
    'For editors, having quick access to multiple sizes lets you match the exact dimensions your layout expects — a 480×360 preview strip, a 320×180 grid card, or a 1280×720 hero — without an extra resize step.',
];
const TIPS = [
    'Use the max-resolution thumbnail for hero images — it’s the sharpest.',
    'If a size is missing, the video is likely older or low-resolution; fall back to hqdefault (480×360).',
    'For social embeds, sddefault (640×480) is usually the safest widely-supported size.',
];

export default function ThumbnailDownloaderPage() {
    const [url, setUrl] = useState('');
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState('');
    const [info, setInfo] = useState(null);

    const fetchInfo = async () => {
        if (!url.trim()) return;
        setStatus('loading');
        setError('');
        setInfo(null);
        try {
            const data = await getThumbnailInfo(url.trim());
            setInfo(data);
            setStatus('done');
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.message || 'Something went wrong.';
            setError(msg);
            setStatus('error');
            toast.error(msg);
        }
    };

    const structured = {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'UtilityTool — YouTube Thumbnail Downloader',
        applicationCategory: 'Utility',
        operatingSystem: 'Any',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    };

    return (
        <>
            <Seo
                title="YouTube Thumbnail Downloader"
                description="Grab high-resolution thumbnails from any public YouTube video. Supports Shorts, embeds, and short links. Free, no signup."
                canonical="/tools/thumbnail-downloader"
                structuredData={structured}
            />
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-14">
                <Breadcrumb items={[{ label: 'Tools', to: '/tools' }, { label: 'Thumbnail Downloader' }]} />
                <div className="mt-4 flex items-start gap-4">
                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--primary))]">
                        <Youtube className="w-6 h-6" />
                    </span>
                    <div>
                        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">YouTube Thumbnail Downloader</h1>
                        <p className="mt-2 text-slate-600 max-w-2xl">
                            Grab high-resolution thumbnails from any public YouTube video.
                            Supports Shorts, embeds, and short links.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700">YouTube</Badge>
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700">All sizes</Badge>
                        </div>
                    </div>
                </div>

                <Card className="mt-8 p-5 sm:p-6 border-border/80">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="flex-1">
                            <Label htmlFor="th-url">YouTube URL or video ID</Label>
                            <Input
                                id="th-url"
                                data-testid="tool-thumb-url"
                                value={url}
                                onChange={(e) => setUrl(e.target.value)}
                                placeholder="https://www.youtube.com/watch?v=…"
                                className="mt-1.5"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') fetchInfo();
                                }}
                            />
                        </div>
                        <div className="sm:self-end">
                            <Button
                                onClick={fetchInfo}
                                disabled={!url.trim() || status === 'loading'}
                                data-testid="tool-thumb-fetch"
                                className="h-11 bg-slate-900 hover:bg-slate-800 text-white w-full sm:w-auto"
                            >
                                {status === 'loading' ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Fetching…
                                    </>
                                ) : (
                                    'Fetch thumbnails'
                                )}
                            </Button>
                        </div>
                    </div>

                    {status === 'error' && error ? (
                        <Alert variant="destructive" className="mt-6" data-testid="tool-thumb-error">
                            <AlertCircle className="w-4 h-4" />
                            <AlertTitle>We couldn’t fetch that video</AlertTitle>
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    ) : null}

                    {info ? (
                        <div className="mt-6" data-testid="tool-thumb-results">
                            <p className="text-sm text-slate-600">
                                Video ID:{' '}
                                <a
                                    href={info.watch_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="font-mono text-[hsl(var(--primary))] hover:underline"
                                >
                                    {info.video_id}
                                </a>
                            </p>
                            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {info.thumbnails.map((t) => (
                                    <div
                                        key={t.quality}
                                        className="rounded-xl border border-border/70 bg-white overflow-hidden"
                                        data-testid={`tool-thumb-item-${t.quality}`}
                                    >
                                        <div className="aspect-video bg-slate-100 grid place-content-center">
                                            {t.available ? (
                                                <img
                                                    src={t.url}
                                                    alt={`${t.label} thumbnail`}
                                                    className="w-full h-full object-cover"
                                                    loading="lazy"
                                                />
                                            ) : (
                                                <span className="text-xs text-slate-500">Not available</span>
                                            )}
                                        </div>
                                        <div className="p-3 flex items-center justify-between gap-2">
                                            <div>
                                                <p className="text-sm font-medium text-slate-900">{t.label}</p>
                                                <p className="text-xs text-slate-500">{t.width} × {t.height}</p>
                                            </div>
                                            <Button
                                                asChild
                                                size="sm"
                                                disabled={!t.available}
                                                variant={t.available ? 'default' : 'secondary'}
                                                className={t.available ? 'bg-[hsl(var(--primary))] hover:bg-[hsl(var(--link-hover))] text-white' : ''}
                                            >
                                                {t.available ? (
                                                    <a
                                                        href={thumbnailDownloadUrl(info.video_id, t.quality)}
                                                        data-testid={`tool-thumb-download-${t.quality}`}
                                                    >
                                                        <Download className="w-3.5 h-3.5 mr-1.5" /> Download
                                                    </a>
                                                ) : (
                                                    <span>Unavailable</span>
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null}
                </Card>

                <div className="mt-6 rounded-xl border border-border/70 bg-[hsl(var(--surface-2))] p-4">
                    <div className="flex items-center gap-2 text-slate-800">
                        <ShieldCheck className="w-4 h-4 text-[hsl(var(--primary))]" />
                        <span className="text-sm font-semibold">We don’t store URLs</span>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">
                        Your URL is used once, live, and is never logged or shared.
                    </p>
                </div>

                <ToolLongForm howItWorks={HOW} benefits={BENEFITS} tips={TIPS} />
                <AdSlot placement="tool" />
                <ToolFAQ items={FAQS} />
            </section>
        </>
    );
}
